import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { complaintAPI, technicalAPI } from '../../services/api';
import { LogOut, Menu, ArrowLeft, ArrowLeftRight } from 'lucide-react';
import NotificationBell from './TechnicalNotificationBell';
import ProfileMenu from '../common/ProfileMenu';
import ViewModeToggle from '../common/ViewModeToggle';
import { navItems, getPageTitle } from './technicalNavConfig';
import { useCompanyIdentity } from '../../hooks/useCompanyIdentity';
import { IS_LAXMI_HOST, DEMO_BRAND } from '../../utils/demo';
import hasRole from '../../utils/hasRole';
import useAppearance from '../../theme/useAppearance';

// Sidebar for the technical-team login — deliberately a small, separate shell
// (not AdminLayout) since technical users have no business seeing the customer
// search bar, floating pay button, or anything billing-related that AdminLayout
// carries. Only the network-hardware modules + a read-only connections roster +
// their own assigned complaints queue.
export default function TechnicalLayout({ viewMode, onToggleViewMode }) {
  const { user, logout } = useAuth();
  useAppearance();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openComplaints, setOpenComplaints] = useState(0);
  const [activeAlarms, setActiveAlarms] = useState(0);
  // Technical team is the broadband/network side of the business — Angel Internet
  // Services' own identity, not catv's Laxmi Cable Vision (used by the agent app).
  const brand = useCompanyIdentity('broadband');

  useEffect(() => {
    complaintAPI.getAll({ status: 'open' }).then(({ data }) => setOpenComplaints(data.data.length)).catch(() => {});
    // Critical + major only, same reasoning as the notification bell — the nav badge counts
    // faults needing a technician, not routine customer power cuts (which stay on the
    // Alarms page behind its "Show minor" toggle).
    technicalAPI.getAlarms({ status: 'active', severity: 'critical,major' })
      .then(({ data }) => setActiveAlarms(data.data.length)).catch(() => {});
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const pageTitle = getPageTitle(location.pathname);

  return (
    <div className="admin-layout">
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 90 }}
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <img src={brand.logo || (IS_LAXMI_HOST ? '/LCV.png?v=1' : DEMO_BRAND.logo)} alt={brand.name || (IS_LAXMI_HOST ? 'Laxmi Cable Vision' : DEMO_BRAND.name)} style={{ height: 72, objectFit: 'contain', maxWidth: '100%' }} />
          <div style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--sidebar-brand, var(--c-ffffff))', textAlign: 'center', marginTop: 6 }}>
            {brand.name || (IS_LAXMI_HOST ? 'Angel Internet Services' : DEMO_BRAND.name)}
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section-label">TECHNICAL TEAM</div>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon size={16} />
              {item.label}
              {item.badgeKey === 'complaints' && openComplaints > 0 && (
                <span className="badge">{openComplaints}</span>
              )}
              {item.badgeKey === 'alarms' && activeAlarms > 0 && (
                <span className="badge">{activeAlarms}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="avatar-circle">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="sidebar-user-info">
              <p>{user?.name}</p>
              <span>{user?.role}</span>
            </div>
          </div>
          <button className="btn-logout" onClick={handleLogout}>
            <LogOut size={13} style={{ display: 'inline', marginRight: 6 }} />
            Sign Out
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <div className="flex items-center gap-3" style={{ flexShrink: 0 }}>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="mobile-menu-btn">
              <Menu size={20} />
            </button>
            {/* Only admin/staff arrived here from the main portal (via the "Network Overview"
                sidebar link) — a real technical-role login has nowhere else to go, so this
                stays hidden for them. */}
            {user?.role !== 'technical' && (
              <button
                onClick={() => navigate('/dashboard')}
                title="Back to Admin Portal"
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                  padding: '5px 10px', fontSize: '.8rem', fontWeight: 600,
                  color: 'var(--text)', cursor: 'pointer',
                }}
              >
                <ArrowLeft size={14} /> Back to Admin
              </button>
            )}
            {hasRole(user, 'agent') && (
              <button
                onClick={() => navigate('/agent')}
                title="Switch to Agent App"
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: 'none', border: '1px solid var(--border)', borderRadius: 6,
                  padding: '5px 10px', fontSize: '.8rem', fontWeight: 600,
                  color: 'var(--text)', cursor: 'pointer',
                }}
              >
                <ArrowLeftRight size={14} /> Switch to Agent App
              </button>
            )}
            <span className="topbar-title">{pageTitle}</span>
          </div>

          <div className="topbar-right">
            <ViewModeToggle viewMode={viewMode} onToggle={onToggleViewMode} />
            <NotificationBell />
            <ProfileMenu user={user} onLogout={handleLogout} size={30} />
          </div>
        </header>

        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
